// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The playfield's static furniture — guides, walls and posts (M3-05).
//
// Every collider in the definition that is not a mechanism gets a body here, drawn on exactly
// the line the solver tests against. That is the whole discipline of this file: the rail is
// not *near* the collider, it is centred on it, so a ball that appears to bounce off thin air
// means the machine is wrong rather than the picture.
//
// A device's own colliders are deliberately skipped. A slingshot's face, a drop target's face
// and a gate's arm are parts of a mechanism that moves, and `deviceMeshes.ts` draws them
// where they currently are rather than where they started.

import { Group, type BufferGeometry, type Material } from 'three'
import type { MachineCollider, MachineDefinition } from '@core/machine'
import { postMeshes, railMesh, RAIL_HEIGHT, type PartPalette } from './partsKit'
import { sampleArc, type Point2 } from './railPath'
import { lowerBodyFits } from './authoredLowerAssembly'
import { authoredGuide,authoredGuardContact } from './authoredGuides'
import { authoredPost, postCentre } from './authoredPosts'
import { slingHousing } from './slingAssembly'
import { ownsPortalMount } from './portalMounts'

export interface BuiltRails {
  readonly group: Group
  /** Geometries the caller must dispose. Materials belong to the palette. */
  readonly geometries: readonly BufferGeometry[]
  /** Colliders drawn as nothing, and why. Reported rather than silently absent. */
  readonly skipped: readonly string[]
}

/**
 * Build every static collider on a plane layer.
 *
 * Ramp and wire layers are not drawn from their colliders, because they have none: a ramp is
 * a spline the ball is carried along, and its walls are the layer's own `width` and
 * `wallHeight` (M3-06). A collider on one of those layers would be a machine-definition
 * error, so it is reported here rather than quietly rendered flat on the wood.
 */
export function buildRails(definition: MachineDefinition, palette: PartPalette, illustrated = false, steel?: Material): BuiltRails {
  const group = new Group()
  group.name = 'rails'
  const geometries: BufferGeometry[] = []
  const skipped: string[] = []

  const planeLayers = new Set(
    definition.layers.filter((l) => l.kind === 'plane').map((l) => l.id),
  )

  const layerGroups=new Map<string,Group>()
  for(const layer of definition.layers)if(layer.kind==='plane'&&layer.z!==0){const raised=new Group();raised.name=layer.id+'.furniture';raised.position.z=layer.z;group.add(raised);layerGroups.set(layer.id,raised)}
  for (const collider of definition.colliders) {
    const parentGroup=layerGroups.get(collider.layer)??group
    if (collider.device !== undefined) continue
    if(ownsPortalMount(definition,collider))continue
    if(collider.kind==='circle'&&definition.lamps?.some(l=>l.kind==='flasher'&&(l.id?collider.id===l.id+'Base':collider.id.endsWith('Base')&&Math.abs(collider.radius-(l.radius??.026)*.027/.026)<1e-9)&&JSON.stringify(collider.centre)===JSON.stringify(l.position)))continue
    if(authoredGuardContact(definition,collider))continue
    if (!planeLayers.has(collider.layer)) {
      skipped.push(`${collider.id}: on ${collider.layer}, which is not a plane layer`)
      continue
    }

    const height = RAIL_HEIGHT[collider.material]
    const material = layerGroups.has(collider.layer)&&collider.material==='plastic'?palette.acrylic('rampClearSheet'):collider.material==='metal'||collider.material==='wire'
      ? palette.guideSteel(steel)
      : palette.get(collider.id==='slingBodyLeft'||collider.id==='slingBodyRight'||illustrated && lowerBodyFits(definition, collider.id) ? 'hardwareRubber' : collider.material)
    const housing=slingHousing(definition,collider,palette)
    if(housing!==undefined){parentGroup.add(housing);geometries.push(housing.geometry);continue}

    if (collider.kind === 'circle') {
      const fitted = authoredPost(definition, collider, palette, illustrated, steel)
      const built = fitted ?? postMeshes(collider.radius, collider.material, palette)
      const at = postCentre(collider, definition)
      geometries.push(...built.geometries)
      for (const mesh of built.meshes) {
        mesh.position.set(at[0], at[1], 0)
        parentGroup.add(mesh)
      }
      continue
    }

    const fitted = authoredGuide(definition, collider, palette, illustrated, steel)
    if (fitted !== undefined) {
      geometries.push(fitted.geometry)
      parentGroup.add(fitted)
      continue
    }
    const points = pathOf(collider)
    const mesh = railMesh(points, material, height,collider.thickness===undefined?undefined:collider.thickness/2)
    mesh.name = collider.id
    geometries.push(mesh.geometry)
    parentGroup.add(mesh)
  }

  return { group, geometries, skipped }
}

/** A collider's centre-line, whatever shape it was stated as. */
function pathOf(collider: Exclude<MachineCollider, { kind: 'circle' }>): Point2[] {
  switch (collider.kind) {
    case 'segment':
      return [collider.a, collider.b]
    case 'polyline':
      return [...collider.points]
    case 'arc':
      return sampleArc(collider.centre, collider.radius, collider.angleFrom, collider.angleTo)
  }
}
